import { Controller } from "@hotwired/stimulus"
import { TerminalReplay } from "../modules/terminal_replay"

// Thin coordinator for the replayable terminal widget. It reads the inline cast
// JSON, drives the pure TerminalReplay engine against the <pre> output, and
// wires the play/pause, restart and "skip the wait" controls plus a progress
// affordance. The complete transcript is server-rendered inside the <pre> as a
// no-JS fallback; on connect we replay over it, or — under
// prefers-reduced-motion — leave the full transcript untouched and animate
// nothing. Malformed casts are logged and ignored, never thrown.
export default class extends Controller {
  static targets = ["cast", "output", "playPause", "skip", "progress", "progressBar"]

  connect() {
    const cast = this._readCast()
    if (cast == null) return

    try {
      this.replay = new TerminalReplay({
        cast,
        write: (html) => this.outputTarget.insertAdjacentHTML("beforeend", html),
        clear: () => { this.outputTarget.textContent = "" },
        onState: (state) => this._syncControls(state)
      })
    } catch (error) {
      console.warn("[replayable-terminal] ignoring malformed cast:", error.message)
      return
    }

    if (this._prefersReducedMotion()) {
      // Full transcript is already server-rendered — leave it, animate nothing.
      this._syncControls({ status: "done", index: this.replay.total, total: this.replay.total })
      return
    }
    this.replay.play()
  }

  disconnect() {
    if (this.replay) this.replay.pause()
  }

  toggle() {
    if (!this.replay) return
    const status = this.replay.status
    if (status === "playing" || status === "gap") this.replay.pause()
    else if (status === "done") this.restart()
    else if (this._prefersReducedMotion()) this.replay.revealAll()
    else this.replay.play()
  }

  restart() {
    if (!this.replay) return
    if (this._prefersReducedMotion()) this.replay.revealAll()
    else this.replay.restart()
  }

  skip() {
    if (this.replay) this.replay.skipGap()
  }

  _readCast() {
    if (!this.hasCastTarget) {
      console.warn("[replayable-terminal] no cast target present")
      return null
    }
    try {
      return JSON.parse(this.castTarget.textContent)
    } catch (error) {
      console.warn("[replayable-terminal] cast JSON did not parse:", error.message)
      return null
    }
  }

  _syncControls(state) {
    const playing = state.status === "playing" || state.status === "gap"
    if (this.hasPlayPauseTarget) {
      this.playPauseTarget.textContent =
        playing ? "Pause" : state.status === "done" ? "Replay" : "Play"
      this.playPauseTarget.setAttribute("aria-pressed", String(playing))
    }
    if (this.hasSkipTarget) {
      const gap = state.status === "gap"
      this.skipTarget.disabled = !gap
      this.skipTarget.hidden = !gap
    }
    if (this.hasProgressTarget && state.total > 0) {
      const pct = Math.round((Math.min(state.index, state.total) / state.total) * 100)
      this.progressTarget.style.width = `${pct}%`
      if (this.hasProgressBarTarget) {
        this.progressBarTarget.setAttribute("aria-valuenow", String(pct))
      }
    }
    if (this.hasOutputTarget) {
      this.outputTarget.classList.toggle("is-playing", playing)
    }
  }

  _prefersReducedMotion() {
    return typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
  }
}
